// متن فارسی خطا برای toast و صفحه خطای هر دو اپ؛ بدون وابستگی سروری تا در مرورگر هم بارگیری شود
import { ORPCError } from "@orpc/client";
import { z } from "zod";

const hasPersian = (text: string) => /\p{Script=Arabic}/u.test(text);

const MESSAGES = {
  network: "ارتباط با سرور برقرار نشد؛ اینترنت را بررسی و دوباره تلاش کنید.",
  timeout: "پاسخ سرور خیلی طول کشید؛ دوباره تلاش کنید.",
  server: "خطای سرور؛ چند دقیقه بعد دوباره تلاش کنید.",
  rateLimit: "درخواست‌ها زیاد بود؛ یک دقیقه صبر کنید و دوباره تلاش کنید.",
  invalid: "مقدارها معتبر نیست؛ فرم را بررسی کنید.",
  unauthorized: "نشست شما تمام شده است؛ دوباره وارد شوید.",
  forbidden: "اجازه این کار را ندارید.",
  notFound: "مورد خواسته‌شده پیدا نشد؛ شاید حذف شده باشد.",
  conflict: "این مورد هم‌زمان تغییر کرده است؛ صفحه را تازه کنید و دوباره تلاش کنید.",
  unknown: "مشکلی پیش آمد؛ دوباره تلاش کنید.",
} as const;

const issuesSchema = z.object({
  issues: z.array(z.object({ path: z.array(z.string()), message: z.string() })),
});

/** مسیر خطای سرور → name فیلد فرم: ["lines", "0", "amount"] → «lines[0].amount» */
const fieldName = (path: readonly string[]) =>
  path.reduce(
    (name, step) => (/^\d+$/.test(step) ? `${name}[${step}]` : name ? `${name}.${step}` : step),
    "",
  );

/**
 * ورودی ردشده (data.issues سرور): «بودجه از (میلیون تومان): حداکثر ۱٬۰۰۰ است»، تا سه فیلد؛
 * فیلدی که برچسبش پیدا نشود یا پیامش فارسی نباشد کنار گذاشته می‌شود
 */
const issuesText = (
  issues: z.infer<typeof issuesSchema>["issues"],
  labelOf: (name: string) => string | undefined,
) =>
  issues
    .flatMap((issue) => {
      const label = labelOf(fieldName(issue.path));
      return label && hasPersian(issue.message) ? [`${label}: ${issue.message}`] : [];
    })
    .slice(0, 3)
    .join("؛ ");

/**
 * پیام قابل‌فهم برای کاربر: پیام فارسی سرور همان‌طور می‌ماند؛ پیام‌های انگلیسی پیش‌فرض oRPC،
 * قطعی شبکه و پایان مهلت به متن فارسی ثابت تبدیل می‌شوند. پیام خام را فقط در کنسول ثبت کنید.
 * labelOf: برچسب فیلد فرم از روی name آن، تا ورودی ردشده بگوید کدام فیلد چه مشکلی دارد.
 */
export function persianError(
  error: Error | null,
  labelOf: (name: string) => string | undefined = () => undefined,
): string {
  if (error instanceof ORPCError) {
    if (hasPersian(error.message)) return error.message;
    if (error.status === 400) {
      const fields = issuesText(issuesSchema.safeParse(error.data).data?.issues ?? [], labelOf);
      if (fields) return fields;
    }
    const status = error.status;
    if (status === 401) return MESSAGES.unauthorized;
    if (status === 403) return MESSAGES.forbidden;
    if (status === 404) return MESSAGES.notFound;
    if (status === 408 || status === 504) return MESSAGES.timeout;
    if (status === 409) return MESSAGES.conflict;
    if (status === 429) return MESSAGES.rateLimit;
    if (status >= 500) return MESSAGES.server;
    // BAD_REQUEST («Input validation failed») و دیگر خطاهای ورودی
    if (status >= 400) return MESSAGES.invalid;
    return MESSAGES.unknown;
  }
  if (error instanceof Error) {
    // خطای فارسی که کد خود اپ پرتاب کرده (مثلاً پیام Better Auth ترجمه‌شده)
    if (hasPersian(error.message)) return error.message;
    if (error.name === "TimeoutError" || error.name === "AbortError") return MESSAGES.timeout;
    // fetch شکست‌خورده: «Failed to fetch»، «Load failed»، «NetworkError …»
    if (error.name === "TypeError") return MESSAGES.network;
  }
  return MESSAGES.unknown;
}
